/**
 * App container.
 * @module components/theme/App/App
 */

import React, { Component } from 'react';
import jwtDecode from 'jwt-decode';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { asyncConnect, Helmet } from '@plone/volto/helpers';
import { renderRoutes } from 'react-router-config';
import { Slide, ToastContainer, toast } from 'react-toastify';
import split from 'lodash/split';
import join from 'lodash/join';
import trim from 'lodash/trim';
import cx from 'classnames';
import config from '@plone/volto/registry';
import { PluggablesProvider } from '@plone/volto/components/manage/Pluggable';
import { visitBlocks } from '@plone/volto/helpers/Blocks/Blocks';
import { injectIntl } from 'react-intl';

import Error from '@plone/volto/error';

import * as Sentry from '@sentry/react';

import {
  Icon,
  OutdatedBrowser,
  AppExtras,
  SkipLinks,
} from '@plone/volto/components';
import {
  BodyClass,
  getBaseUrl,
  getId,
  getView,
  hasApiExpander,
  isCmsUi,
} from '@plone/volto/helpers';
import {
  getBreadcrumbs,
  getContent,
  getNavigation,
  getTypes,
  getWorkflow,
} from '@plone/volto/actions';

import clearSVG from '@plone/volto/icons/clear.svg';
import MultilingualRedirector from '@plone/volto/components/theme/MultilingualRedirector/MultilingualRedirector';
import WorkingCopyToastsFactory from '@plone/volto/components/manage/WorkingCopyToastsFactory/WorkingCopyToastsFactory';
import LockingToastsFactory from '@plone/volto/components/manage/LockingToastsFactory/LockingToastsFactory';
import { GoogleAnalyticsDataForm } from '@fhnw/components/GoogleAnalyticsDataForm/GoogleAnalyticsDataForm.tsx';
import { DataLayerAnalytics } from '@fhnw/components/DataLayerAnalytics/DataLayerAnalytics.tsx';
import { shouldRenderDataLayerAnalytics } from '@fhnw/components/DataLayerAnalytics/analyticsRoute.ts';
import CookieConsent from '@fhnw/components/CookieConsent/CookieConsent.tsx';
import { VocabProvider } from '@fhnw/components/VocabContext/VocabContext.tsx';
import { IntroView, IntroEdit } from '@fhnw/components/Intro';
import {
  AnchorNavigationView,
  AnchorNavigationEdit,
} from '@fhnw/components/AnchorNavigation';
import { Header } from '@fhnw/components/Header';
import { Footer } from '@fhnw/components/Footer';
import { DesignSystemProvider } from '@designsystem/stories/05-templates/DesignSystemProvider';
import {
  OccasionRegistrationProvider,
  OccasionRegistrationPanel,
} from '@fhnw/components/OccasionRegistrationPanel';

import { getLangFromPath } from '@fhnw/helpers/getLangFromPath.ts';
import { DesignSystemBodyClass } from '@fhnw/helpers/DesignSystem/BodyClass';
import HashScrollHandler from '@fhnw/components/HashScrollHandler/HashScrollHandler.tsx';
import SchemaOrg from '@fhnw/components/SchemaOrg/SchemaOrg';
import Cookies from 'universal-cookie';

const cookies = new Cookies();
const token = cookies.get('auth_token');
const user = token ? jwtDecode(token).sub : 'Anonymous';

const env = (typeof window !== 'undefined' ? window : process)?.env;

const FHNW_ENV = env?.RAZZLE_FHNW_ENV || 'NO_ENV';
const FORCE_SENTRY = env?.RAZZLE_FORCE_SENTRY || 0;
const FORCE_SIDEBAR = [
  'DegreeProgram',
  'ContinuingEducation',
  'FHNWEvent',
  'InfoEvent',
  'Contact',
  'TeamContact',
  'ResearchArea',
  'ResearchFocus',
  'Service',
  'Specialization',
  'Project',
];

if (
  FHNW_ENV === 'PROD' ||
  FHNW_ENV === 'TEST' ||
  FHNW_ENV === 'DEV' ||
  FORCE_SENTRY
) {
  Sentry.init({
    dsn: 'https://fdd4631abf6107ba9d8ff3b8dddc1be7@o4508420237492224.ingest.de.sentry.io/4509825609433168',
    sendDefaultPii: true,
    enableLogs: true,
    environment: FHNW_ENV,
  });

  Sentry.setContext('Context', {
    user,
    release_git_tag: env?.RAZZLE_RELEASE_GIT_TAG || 'NO_TAG',
    release_git_branch: env?.RAZZLE_RELEASE_GIT_BRANCH || 'NO_BRANCH',
  });
}

/**
 * @export
 * @class App
 * @extends {Component}
 */
export class App extends Component {
  /**
   * Property types.
   * @property {Object} propTypes Property types.
   * @static
   */
  static propTypes = {
    pathname: PropTypes.string.isRequired,
  };

  state = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  constructor(props) {
    super(props);
    this.mainRef = React.createRef();
  }

  /**
   * @method componentWillReceiveProps
   * @param {Object} nextProps Next properties
   * @returns {undefined}
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.pathname !== this.props.pathname) {
      if (this.state.hasError) {
        this.setState({ hasError: false });
      }
    }
  }

  /**
   * ComponentDidCatch
   * @method ComponentDidCatch
   * @param {string} error  The error
   * @param {string} info The info
   * @returns {undefined}
   */
  componentDidCatch(error, info) {
    this.setState({ hasError: true, error, errorInfo: info });
    config.settings.errorHandlers.forEach((handler) => handler(error));
  }

  dispatchContentClick = (event) => {
    if (event.target === event.currentTarget) {
      const rect = this.mainRef.current.getBoundingClientRect();
      if (event.clientY > rect.bottom) {
        document.dispatchEvent(new Event('voltoClickBelowContent'));
      }
    }
  };

  /**
   * Render method.
   * @method render
   * @returns {string} Markup for the component.
   */
  render() {
    const { views } = config;
    const path = getBaseUrl(this.props.pathname);
    const action = getView(this.props.pathname);
    const id = getId(this.props.pathname);
    const isCmsUI = isCmsUi(this.props.pathname);
    const showDataLayerAnalytics = shouldRenderDataLayerAnalytics({
      action,
      isCmsUI,
    });
    const ConnectionRefusedView = views.errorViews.ECONNREFUSED;
    const env = config?.settings?.['FHNW_ENV'];
    const isAdd = action === 'add';
    const isViewOrEdit =
      (action === 'view' || action === 'edit' || action === 'add') &&
      id !== 'manage-translations' &&
      id !== 'historyview' &&
      id !== 'login';
    const Intro = action === 'edit' || action === 'add' ? IntroEdit : IntroView;
    const AnchorNavigation =
      action === 'edit' || action === 'add'
        ? AnchorNavigationEdit
        : AnchorNavigationView;
    // On add, use form data instead of parent content for sidebar/anchor decisions
    const effectiveContent =
      isAdd &&
      this.props.formData &&
      Object.keys(this.props.formData).length > 0
        ? this.props.formData
        : this.props.content;
    const effectiveType = isAdd
      ? new URLSearchParams(this.props.location?.search).get('type')
      : effectiveContent?.['@type'];
    const show_anchor =
      isViewOrEdit && effectiveContent?.show_anchor_navigation;
    const show_sidebar =
      isViewOrEdit && (show_anchor || FORCE_SIDEBAR.includes(effectiveType));

    const languageFromPath = getLangFromPath(this.props.pathname);

    const language =
      this.props.content?.language?.token ??
      languageFromPath ??
      this.props.intl?.locale;

    return (
      <VocabProvider>
        <PluggablesProvider>
          <DesignSystemProvider>
            <OccasionRegistrationProvider>
              <DesignSystemBodyClass />
              <HashScrollHandler
                pathname={this.props.pathname}
                content={this.props.content}
              />
              <SchemaOrg type={effectiveType} content={effectiveContent} />
              {(env === 'PROD' || env === 'TEST') && <CookieConsent />}
              {language && (
                <Helmet>
                  <html lang={language} />
                </Helmet>
              )}
              <BodyClass className={`view-${action}view`} />

              {/* Body class depending on content type */}
              {this.props.content && this.props.content['@type'] && (
                <BodyClass
                  className={cx(
                    `contenttype-${this.props.content['@type']
                      .replace(' ', '-')
                      .toLowerCase()}`,
                    `env-${env}`,
                  )}
                />
              )}

              {/* Body class depending on sections */}
              <BodyClass
                className={cx({
                  [trim(join(split(this.props.pathname, '/'), ' section-'))]:
                    this.props.pathname !== '/',
                  siteroot: this.props.pathname === '/',
                  'is-authenticated': !!this.props.token,
                  'is-anonymous': !this.props.token,
                  'cms-ui': isCmsUI,
                  'public-ui': !isCmsUI,
                })}
              />
              <MultilingualRedirector
                navigation={this.props.navigation}
                pathname={this.props.pathname}
                contentLanguage={language}
              >
                <SkipLinks />
                <Header content={this.props.content} />
                <main
                  id="content-area"
                  ref={this.mainRef}
                  className="content-area"
                  role="main"
                  tabIndex="-1"
                >
                  <div id="navigation" tabIndex="-1">
                    {false && <Header pathname={path} />}
                  </div>
                  <div
                    className="content-slot"
                    onClick={this.dispatchContentClick}
                    role="presentation"
                  >
                    {this.props.content && (
                      <Intro
                        content={this.props.content}
                        isViewOrEdit={isViewOrEdit}
                      />
                    )}
                    <OutdatedBrowser />
                    {this.props.connectionRefused ? (
                      <div className="page__section page__section--inline">
                        <ConnectionRefusedView />
                      </div>
                    ) : this.state.hasError ? (
                      <div className="page__section page__section--inline">
                        <Error
                          message={this.state.error.message}
                          stackTrace={this.state.errorInfo.componentStack}
                        />
                      </div>
                    ) : (
                      <article
                        id="page-document"
                        className={`page page--${show_sidebar ? 'has-sidebar' : 'no-sidebar'}`}
                      >
                        {show_sidebar && (
                          <aside className="page__sidebar">
                            {show_anchor && (
                              <AnchorNavigation content={this.props.content} />
                            )}
                          </aside>
                        )}
                        <div className="page__content">
                          <Intro
                            partial
                            content={this.props.content}
                            isViewOrEdit={isViewOrEdit}
                          />
                          {renderRoutes(this.props.route.routes, {
                            staticContext: this.props.staticContext,
                          })}
                        </div>
                      </article>
                    )}
                    <GoogleAnalyticsDataForm content={this.props.content} />
                    {showDataLayerAnalytics && (
                      <DataLayerAnalytics
                        content={this.props.content}
                        pathname={this.props.pathname}
                        search={this.props.location?.search}
                      />
                    )}
                  </div>
                </main>
                <Footer content={this.props.content} />
              </MultilingualRedirector>

              <LockingToastsFactory
                content={this.props.content}
                user={this.props.userId}
              />
              <WorkingCopyToastsFactory content={this.props.content} />
              <ToastContainer
                position={toast.POSITION.BOTTOM_CENTER}
                hideProgressBar
                transition={Slide}
                autoClose={5000}
                closeButton={
                  <Icon
                    className="toast-dismiss-action"
                    name={clearSVG}
                    size="18px"
                  />
                }
              />
              <AppExtras {...this.props} />
              <OccasionRegistrationPanel />
            </OccasionRegistrationProvider>
          </DesignSystemProvider>
        </PluggablesProvider>
      </VocabProvider>
    );
  }
}

export const __test__ = connect(
  (state, props) => ({
    pathname: props.location.pathname,
    token: state.userSession.token,
    content: state.content.data,
    formData: state.form?.global,
    apiError: state.apierror.error,
    connectionRefused: state.apierror.connectionRefused,
  }),
  {},
)(App);

export const fetchContent = async ({ store, location }) => {
  const content = await store.dispatch(
    getContent(getBaseUrl(location.pathname)),
  );

  const promises = [];
  const { blocksConfig } = config.blocks;

  const visitor = ([id, data]) => {
    const blockType = data['@type'];
    // START CUSTOMIZATION
    // Skip block types without a blocksConfig registration (e.g. the
    // accordion's internal "accordionPanel" containers). Without this guard
    // the destructuring throws and aborts the SSR data fetching for all
    // blocks on the page. Upstream fix: plone/volto#7096.
    const block = blocksConfig[blockType];
    if (!block) return;
    const { getAsyncData } = block;
    // END CUSTOMIZATION
    if (getAsyncData) {
      const p = getAsyncData({
        store,
        dispatch: store.dispatch,
        path: location.pathname,
        location,
        id,
        data,
        blocksConfig,
        content,
      });
      if (!p?.length) {
        throw new Error(
          'You should return a list of promises from getAsyncData',
        );
      }
      promises.push(...p);
    }
  };

  visitBlocks(content, visitor);

  await Promise.allSettled(promises);

  return content;
};

export function connectAppComponent(AppComponent) {
  return compose(
    asyncConnect([
      {
        key: 'breadcrumbs',
        promise: ({ location, store: { dispatch } }) => {
          // Do not trigger the breadcrumbs action if the expander is present
          if (
            __SERVER__ &&
            !hasApiExpander('breadcrumbs', getBaseUrl(location.pathname))
          ) {
            return dispatch(getBreadcrumbs(getBaseUrl(location.pathname)));
          }
        },
      },
      {
        key: 'content',
        promise: ({ location, store }) =>
          __SERVER__ && fetchContent({ store, location }),
      },
      {
        key: 'navigation',
        promise: ({ location, store: { dispatch } }) => {
          // Do not trigger the navigation action if the expander is present
          if (
            __SERVER__ &&
            !hasApiExpander('navigation', getBaseUrl(location.pathname))
          ) {
            return dispatch(
              getNavigation(
                getBaseUrl(location.pathname),
                config.settings.navDepth,
              ),
            );
          }
        },
      },
      {
        key: 'types',
        promise: ({ location, store: { dispatch } }) => {
          // Do not trigger the types action if the expander is present
          if (
            __SERVER__ &&
            !hasApiExpander('types', getBaseUrl(location.pathname))
          ) {
            return dispatch(getTypes(getBaseUrl(location.pathname)));
          }
        },
      },
      {
        key: 'workflow',
        promise: ({ location, store: { dispatch } }) =>
          __SERVER__ && dispatch(getWorkflow(getBaseUrl(location.pathname))),
      },
    ]),
    injectIntl,
    connect(
      (state, props) => ({
        pathname: props.location.pathname,
        token: state.userSession.token,
        userId: state.userSession.token
          ? jwtDecode(state.userSession.token).sub
          : '',
        content: state.content.data,
        formData: state.form?.global,
        apiError: state.apierror.error,
        connectionRefused: state.apierror.connectionRefused,
      }),
      null,
    ),
  )(AppComponent);
}

export default connectAppComponent(App);
